import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { createGlobalStyle, keyframes } from 'styled-components'
import { useAuth } from '../../hooks/useAuth'

// ─── Tokens ───────────────────────────────────────────────
const tokens = {
  purple900: '#2d1b4e',
  purple700: '#4a2570',
  purple600: '#6b3a8a',
  purple400: '#9b6bb5',
  purple200: '#c4aed4',
  purple100: '#e8d8f0',
  purple50: '#faf7fc',
  pink200: '#f5d5e8',
  pink100: '#fdeef7',
}

// ─── Global ───────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; }
`

// ─── Animations ───────────────────────────────────────────
const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
`

// ─── Layout ───────────────────────────────────────────────
const Root = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${tokens.purple50};
  position: relative;
  overflow: hidden;
`

const Orb = styled.div<{
  size: number
  top?: string
  bottom?: string
  left?: string
  right?: string
  color: string
}>`
  position: absolute;
  width: ${p => p.size}px;
  height: ${p => p.size}px;
  border-radius: 50%;
  background: radial-gradient(circle, ${p => p.color} 0%, transparent 70%);
  top: ${p => p.top ?? 'auto'};
  bottom: ${p => p.bottom ?? 'auto'};
  left: ${p => p.left ?? 'auto'};
  right: ${p => p.right ?? 'auto'};
  pointer-events: none;
`

// ─── Left panel ───────────────────────────────────────────
const LeftPanel = styled.div`
  width: 42%;
  background: linear-gradient(
    160deg,
    ${tokens.purple900} 0%,
    ${tokens.purple700} 50%,
    ${tokens.purple600} 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px 44px;
  position: relative;
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
  &::before {
    width: 260px;
    height: 260px;
    top: -60px;
    right: -80px;
  }
  &::after {
    width: 160px;
    height: 160px;
    bottom: 60px;
    left: -40px;
    border-color: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const BrandMark = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);

  span {
    font-family: 'Cormorant Garamond', serif;
    font-size: 18px;
    font-weight: 500;
    color: white;
  }
`

const Headline = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: 38px;
  font-weight: 300;
  color: white;
  line-height: 1.2;
  letter-spacing: -0.5px;

  em {
    font-style: italic;
    color: #e8c8f8;
  }
`

const Subtext = styled.p`
  margin-top: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 300;
  line-height: 1.6;
  max-width: 240px;
`

const DotRow = styled.div`
  display: flex;
  gap: 6px;
`

const Dot = styled.div<{ $active?: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => (p.$active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)')};
`

// ─── Right panel ──────────────────────────────────────────
const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
  position: relative;
  z-index: 1;
`

const Card = styled.div`
  width: 100%;
  max-width: 340px;
  animation: ${fadeUp} 0.5s ease both;
`

const CardHeader = styled.div`
  margin-bottom: 36px;
`

const Eyebrow = styled.p`
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${tokens.purple400};
  font-weight: 500;
  margin-bottom: 10px;
`

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 28px;
  font-weight: 400;
  color: ${tokens.purple900};
  line-height: 1.2;
`

// ─── Form elements ────────────────────────────────────────
const Field = styled.div`
  margin-bottom: 18px;
`

const Label = styled.label`
  display: block;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${tokens.purple200};
  font-weight: 500;
  margin-bottom: 7px;
`

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  background: white;
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  color: ${tokens.purple900};
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &::placeholder {
    color: ${tokens.purple200};
  }

  &:focus {
    border-color: ${tokens.purple400};
    box-shadow: 0 0 0 3px rgba(155, 107, 181, 0.12);
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #9b84ad;
  cursor: pointer;

  input {
    accent-color: ${tokens.purple400};
  }
`

const ForgotLink = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: ${tokens.purple400};
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  padding: 0;

  &:hover {
    color: ${tokens.purple600};
  }
`

const ErrorMessage = styled.p`
  font-size: 12px;
  color: #c0436a;
  background: #fdeef4;
  border: 1px solid #f5c0d0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 18px;
`

const Button = styled.button<{ $loading?: boolean }>`
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, ${tokens.purple600} 0%, ${tokens.purple400} 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: ${p => (p.$loading ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$loading ? 0.75 : 1)};
  transition:
    opacity 0.2s,
    transform 0.15s;
  box-shadow: 0 4px 16px rgba(107, 58, 138, 0.28);

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`

const FooterText = styled.p`
  text-align: center;
  font-size: 11px;
  color: ${tokens.purple200};
  margin-top: 24px;
  letter-spacing: 0.3px;
`

// ─── Component ────────────────────────────────────────────
export function LoginPage() {
  const { login, isLoading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await login(email, password)
      navigate('/')
    } catch {
      setError('Invalid email or password. Please try again.')
    }
  }

  return (
    <>
      <GlobalStyle />
      <Root>
        <Orb size={420} top="-120px" left="-80px" color="#e8d5f5" />
        <Orb size={300} bottom="-80px" right="-60px" color="#f5d5e8" />
        <Orb size={180} bottom="40px" left="25%" color="#ddd5f5" />

        <LeftPanel>
          <div>
            <BrandMark>
              <span>K</span>
            </BrandMark>
            <Headline>
              Manage your
              <br />
              <em>kitchen</em>
              <br />
              with care.
            </Headline>
            <Subtext>A thoughtful admin space for your recipe and ingredient catalog.</Subtext>
          </div>
          <DotRow>
            <Dot $active /> <Dot /> <Dot />
          </DotRow>
        </LeftPanel>

        <RightPanel>
          <Card>
            <CardHeader>
              <Eyebrow>Admin Portal</Eyebrow>
              <Title>Welcome back</Title>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              {error && <ErrorMessage>{error}</ErrorMessage>}

              <Field>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@mps.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </Field>

              <Field>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </Field>

              <Row>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={e => setRemember(e.target.checked)}
                  />
                  Remember me
                </CheckLabel>
                <ForgotLink type="button">Forgot password?</ForgotLink>
              </Row>

              <Button type="submit" $loading={isLoading} disabled={isLoading}>
                {isLoading ? 'Signing in…' : 'Sign in to dashboard'}
              </Button>
            </form>

            <FooterText>Meal Plan Solver Admin · Restricted access</FooterText>
          </Card>
        </RightPanel>
      </Root>
    </>
  )
}
